import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MenuPositionX, MenuPositionY } from '@angular/material/menu';
import { DropdownBaseComponent } from '../dropdown-base/dropdown-base.component';
import { ButtonIconSizeMode, ButtonSize, ButtonType, ButtonVerticalSize } from '@anecdotes/design-system/buttons';
import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import { MenuAction } from '../../types';

@Component({
  selector: 'app-action-menu-button',
  templateUrl: './action-menu-button.component.html',
  styleUrls: ['./action-menu-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ActionMenuButtonComponent extends DropdownBaseComponent {
  @ViewChild('menuTrigger', { read: ElementRef })
  private dropdownButton: ElementRef<HTMLElement>;

  @ViewChild('actionButton', { read: ElementRef })
  private actionButton: ElementRef<HTMLElement>;

  @ViewChild(CdkConnectedOverlay, { static: true })
  private cdkConnectedOverlay: CdkConnectedOverlay;

  @HostBinding('class.pointer-events-none')
  @Input()
  disabled: boolean;

  @Input()
  isOnlyIcon: boolean;

  @Input()
  icon: string;

  @Input()
  iconMode: ButtonIconSizeMode = 'default';

  @Input()
  buttonTextTranslationKey: string;

  @Input()
  buttonTranslationKeyParams: object = {};

  @Input()
  listWidth: 'small' | 'medium' | 'asDropdownButton' | 'asActionButton';

  @Input()
  buttonSize: ButtonSize = 'medium';

  @Input()
  btnVerticalSize: ButtonVerticalSize = 'medium';

  @Input()
  type: ButtonType | 'dropdown' = 'primary';

  @Input()
  bottomMenuActions: MenuAction[];

  @Input()
  menuPositionX: MenuPositionX = 'after';

  @Input()
  menuPositionY: MenuPositionY = 'above';

  @Input()
  loading: boolean;

  @Input()
  tooltipTextTranslationKey: string;

  @Input()
  placement: string;

  @Input()
  dropdownClasses = '';

  @Input()
  overlayPosition?: ConnectedPosition;

  @Input()
  maxVisibleItems: number;

  @Input()
  truncateTrigger: boolean;

  @Input()
  displayArrow: boolean;

  @Input()
  rotateArrow = true;

  @Input()
  required = false;

  @Input()
  buttonClasses = '';

  @Output()
  readonly chosenAction = new EventEmitter<MenuAction>();

  get optionButtonsWidth(): number {
    if (this.listWidth === 'asDropdownButton') {
      return this.dropdownButton ? this.dropdownButton.nativeElement.getBoundingClientRect().width : 0;
    }

    if (this.listWidth === 'asActionButton') {
      return this.actionButton.nativeElement.getBoundingClientRect().width ?? 0;
    }

    return null;
  }

  get backdropHeight(): number {
    if (this.maxVisibleItems) {
      return this.maxVisibleItems * 40;
    }
  }

  constructor(cd: ChangeDetectorRef) {
    super(cd);
  }

  open(): void {
    if (this.disabled || this.isOpen) {
      return;
    }

    this.menuOpened();
  }

  @HostListener('document:keydown.escape')
  close(): void {
    if (!this.isOpen) {
      return;
    }

    this.menuClosed();
  }

  updateOverlayPosition(): void {
    this.cdkConnectedOverlay.overlayRef.updatePosition();
  }

  protected getButtonType(): ButtonType {
    return this.type as ButtonType;
  }
}
